import React, { useEffect, useRef, useState } from "react";
import "./ModelPicker.css";

/**
 * Lightweight model dropdown — only Anthropic models, only meaningful when
 * the user is on a system-default agent. Lives bottom-right of the composer,
 * mirroring Chat.js's floating dropdown shape but with our token system.
 */
export default function ModelPicker({
  models,
  value,
  onChange,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const active = models.find((m) => m.id === value) || models[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!models || models.length === 0) return null;

  return (
    <div
      ref={rootRef}
      className={`model-picker ${open ? "open" : ""} ${disabled ? "disabled" : ""}`}
    >
      <button
        type="button"
        className="model-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={disabled ? "Model is fixed by the selected agent" : "Choose model"}
      >
        <span className="model-picker-label">{active?.label || "Model"}</span>
        <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.3"
                fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul className="model-picker-menu" role="listbox">
          {models.map((m) => (
            <li
              key={m.id}
              role="option"
              aria-selected={m.id === value}
              className={`model-picker-option ${m.id === value ? "selected" : ""}`}
              onClick={() => { onChange?.(m.id); setOpen(false); }}
            >
              <span className="model-picker-option-label">{m.label}</span>
              {m.is_default && (
                <span className="model-picker-option-tag">default</span>
              )}
              {m.id === value && (
                <svg className="model-picker-check" width="12" height="12" viewBox="0 0 14 14" aria-hidden="true">
                  <path d="M3 7.5 6 10.5 11 4.5" stroke="currentColor" strokeWidth="1.6"
                        fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
